<template>
  <soha-page-loading v-if="loading" />
  <div class="card card-custom gutter-b example example-compact p-8" v-else>
    <div class="row m-0">
      <span class="soha-page-title">
        {{
          editable
            ? $t(`${I18nPrefix}EDIT_PAGE_TITLE`)
            : $t(`${I18nPrefix}CREATE_PAGE_TITLE`)
        }}
      </span>
    </div>
    <div class="row mx-0 mt-10">
      <div class="col-12">
        <soha-text-field
          name="cardName"
          :placeholder="$t(`${I18nPrefix}NAME`)"
          class="w-100"
          v-model="name.value"
          :error="name.error"
        />
      </div>
      <div class="col-12">
        <soha-text-area
          name="cardDescription"
          class="w-100"
          v-model="description"
          :placeholder="$t(`${I18nPrefix}DESCRIPTION`)"
        />
      </div>
    </div>

    <div class="row m-0">
      <div class="col-12 text-center" v-if="!competencies.length">
        <soha-no-data :message="$t(`${I18nPrefix}EMPTY_COMPETENCIES_LIST`)" />
      </div>
      <div v-else class="col-12">
        <h5 class="my-5">{{ $t(`${I18nPrefix}COMPETENCIES_LIST`) }}</h5>
        <div
          class="col-12 my-4"
          v-for="(competency, index) in competencies"
          :key="index"
        >
          <div class="soha-manage-scoring-card">
            <div class="soha-manage-scoring-card-header">
              <div class="d-flex align-items-center">
                <span class="soha-manage-name d-inline-block">
                  {{ competency.name }}
                </span>
                <label class="soha-competency-weight-label">
                  {{ $t(`${I18nPrefix}WEIGHT`) }}
                  {{ toPersianDigits(competency.weight) }}
                </label>
              </div>

              <soha-menu class="col-2 p-0 d-flex justify-content-start">
                <template v-slot:activator>
                  <span name="manageCardMenu" class="cursor-pointer">
                    <inline-svg src="/media/svg/soha/btn-icons/menu.svg" />
                  </span>
                </template>
                <soha-list>
                  <soha-list-item
                    class="primary-color"
                    :text="$t(`${I18nPrefix}EDIT`)"
                    icon="edit"
                    name="editCompetency"
                    @click="openEditCompetencyModal(competency, index)"
                  />
                  <soha-list-item
                    :text="$t(`${I18nPrefix}DELETE`)"
                    icon="delete"
                    @click="competencies.splice(index, 1)"
                    name="removeCompetency"
                  />
                </soha-list>
              </soha-menu>
            </div>
            <div
              class="row m-0"
              v-if="
                competency.description !=
                `<p class=&quot;ql-direction-rtl ql-align-right&quot;><br></p>`
              "
            >
              <div class="soha-message-primary-text p-0">
                <label class="w-100 mb-4">
                  {{ $t(`${I18nPrefix}SCORING_GUID`) }}
                </label>
                <span v-html="competency.description" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row m-0 px-8">
      <soha-add-new-item
        :text="$t(`${I18nPrefix}ADD_COMPETENCIES`)"
        @addNewItem="$bvModal.show('manage-competency')"
      />
    </div>

    <div class="row mx-0 mt-10 justify-content-end">
      <soha-button
        name="insertCard"
        @click="editable ? editCard() : insertCard()"
        :loading="btnLoading"
        :text="editable ? $t(`${I18nPrefix}EDIT`) : $t(`${I18nPrefix}INSERT`)"
      />
    </div>

    <soha-modal
      size="lg"
      id="manage-competency"
      @hide="clearDataModal"
      :title="
        competencyEdit
          ? $t(`${I18nPrefix}EDIT_COMPETENCIES`)
          : $t(`${I18nPrefix}ADD_COMPETENCIES`)
      "
    >
      <template v-slot:body>
        <div class="row m-0">
          <div class="col-12 px-0 mt-5">
            <soha-text-field
              name="competencyName"
              :placeholder="$t(`${I18nPrefix}COMPETENCY`)"
              class="w-100"
              v-model="competencyName.value"
              :error="competencyName.error"
            />
          </div>
          <div class="col-12 px-0 mt-5">
            <soha-text-field
              name="competencyWeight"
              :placeholder="$t(`${I18nPrefix}WEIGHT`)"
              class="w-100"
              v-model="competencyWeight.value"
              :error="competencyWeight.error"
              type="number"
            />
          </div>
          <div class="col-12 px-0 mt-5">
            <label class="mt-5">
              {{ $t(`${I18nPrefix}SCORING_GUID`) }}
            </label>
            <soha-text-editor
              name="competencyDescription"
              v-model.trim="competencyDescription"
            />
          </div>
        </div>
      </template>
      <template v-slot:footer>
        <soha-button
          name="manageCompetency"
          @click="
            competencyEdit ? editCompetency(competencyIndex) : addCompetency()
          "
          :text="
            competencyEdit ? $t(`${I18nPrefix}EDIT`) : $t(`${I18nPrefix}INSERT`)
          "
        />
      </template>
    </soha-modal>
  </div>
</template>

<script>
import ApiService from "@/core/services/api.service";
import ValidationMixin from "@/mixins/ValidationMixin";
import UIMixin from "@/mixins/UIMixin";
import { mapGetters } from "vuex";

export default {
  name: "ScoringCardCreateEdit",

  mixins: [ValidationMixin, UIMixin],

  data() {
    return {
      I18nPrefix: "PAGES.SETTING.CREATE_EDIT_SCORING_CARD.",
      loading: false,
      btnLoading: false,
      name: {
        value: null,
        error: null,
      },
      description: null,
      competencyDescription: null,
      competencyName: {
        value: null,
        error: null,
      },
      competencyWeight: {
        value: "1",
        error: null,
      },
      competencyEdit: false,
      competencyIndex: null,
      competencies: [],
      cardId: null,
      editable: false,
    };
  },

  mounted() {
    const { params, query } = this.$route;
    this.cardId = params.id || query.id;
    this.editable = !!query.id;
    if (this.cardId) this.getCardInfo();
  },

  methods: {
    clearDataModal() {
      this.competencyName = {
        value: null,
        error: null,
      };
      this.competencyWeight = {
        value: 1,
        error: null,
      };
      this.competencyDescription = null;
    },

    addCompetency() {
      if (this.notValidCompetency()) return;
      this.competencies.push({
        name: this.competencyName.value,
        description: this.competencyDescription,
        weight: this.competencyWeight.value,
      });
      this.$bvModal.hide("manage-competency");
    },

    editCompetency(index) {
      if (this.notValidCompetency()) return;
      this.competencies[index] = {
        name: this.competencyName.value,
        description: this.competencyDescription,
        weight: this.competencyWeight.value,
      };
      this.competencyEdit = false;
      this.$bvModal.hide("manage-competency");
    },

    notValidCompetency() {
      this.competencyName.error = this.validateField(
        this.competencyName.value,
        this.$t(`${this.I18nPrefix}COMPETENCY`)
      );
      this.competencyWeight.error = this.validateField(
        this.competencyWeight.value,
        this.$t(`${this.I18nPrefix}WEIGHT`)
      );
      if (this.competencyWeight.value) {
        this.competencyWeight.error =
          this.competencyWeight.value == 0
            ? this.$t(`${this.I18nPrefix}MINIMUM_WEIGHT_ERROR`)
            : null;
      }
      return this.competencyName.error || this.competencyWeight.error;
    },

    openEditCompetencyModal(competency, index) {
      this.competencyName.value = competency.name;
      this.competencyWeight.value = competency.weight;
      this.competencyDescription = competency.description;
      this.competencyIndex = index;
      this.competencyEdit = true;
      this.$bvModal.show("manage-competency");
    },

    insertCard() {
      if (this.notValidCard()) return;
      this.btnLoading = true;
      ApiService.post("/api/v2/scoringCards", {
        name: this.name.value,
        description: this.description,
        competencies: this.competencies,
        companyId: this.companyId,
      }).then((data) => {
        if (data.data.code == 201) {
          this.successToast(data.data.message);
          this.$router.push({ name: "scoring-card" });
        } else {
          this.errorToast(data.data.message);
        }
        this.btnLoading = false;
      });
    },

    editCard() {
      if (this.notValidCard()) return;
      this.btnLoading = true;
      ApiService.put(`/api/v2/scoringCards/${this.cardId}`, {
        name: this.name.value,
        description: this.description,
        competencies: this.competencies,
      }).then((data) => {
        if (data.data.code == 204) {
          this.successToast(data.data.message);
          this.$router.push({ name: "scoring-card" });
        } else {
          this.errorToast(data.data.message);
        }
        this.btnLoading = false;
      });
    },

    notValidCard() {
      this.name.error = this.validateField(
        this.name.value,
        this.$t(`${this.I18nPrefix}NAME`)
      );
      if (this.name.error) {
        window.scrollTo({ top: 0, behavior: "smooth" });
        return true;
      };
      if (!this.competencies.length) {
        this.errorToast(this.$t(`${this.I18nPrefix}EMPTY_COMPETENCIES_LIST`));
        return true;
      }
      return false;
    },

    getCardInfo() {
      this.loading = true;
      ApiService.get(`/api/v2/scoringCards/${this.cardId}`).then((data) => {
        if (!data.data.code) {
          this.competencies = data.data.competencies;
          this.name.value = this.editable ? data.data.name : null;
          this.description = data.data.description;
        } else {
          this.errorToast(data.data.message);
        }
        this.loading = false;
      });
    },
  },

  computed: {
    ...mapGetters(["companyId", "canDo"]),
  },
};
</script>

<style scoped>
.soha-manage-scoring-card {
  border: 0.077rem solid var(--box-border-color);
  border-radius: 0.462rem;
  padding: 0.769rem 1.154rem;
  height: 100%;
}
.soha-manage-scoring-card-header {
  display: flex;
  border-bottom: 0.077rem solid var(--box-border-color);
  padding-bottom: 0.769rem;
  margin-bottom: 0.769rem;
  color: var(--primary-color);
  justify-content: space-between;
  align-items: center;
}
.soha-manage-name {
  color: var(--primary-color);
  font-weight: bold;
  text-align: right;
  margin-top: 0.231rem;
}
.soha-message-primary-text {
  color: var(--primary-color);
}

.soha-competency-weight-label {
  background: var(--light-secondary-color);
  padding: 0.154rem 0.385rem;
  margin: 0 0.769rem;
  border-radius: 0.615rem;
  color: var(--secondary-color);
}
</style>
